import { z } from 'zod'

export const Region = z.enum(['au', 'uk', 'eu', 'na'])
export type Region = z.infer<typeof Region>

export const Channel = z.enum(['email', 'sms', 'chat', 'voice', 'whatsapp'])
export type Channel = z.infer<typeof Channel>
export const CHANNELS: Channel[] = ['email', 'sms', 'chat', 'voice', 'whatsapp']
export const LIVE_CHANNELS: Channel[] = ['chat', 'voice']

export const StaffRole = z.enum(['admin', 'supervisor', 'specialist', 'consultant'])
export type StaffRole = z.infer<typeof StaffRole>

export const ConversationState = z.enum(['with_ai', 'clarifying', 'waiting_customer', 'waiting_person', 'resolved', 'closed'])
export type ConversationState = z.infer<typeof ConversationState>

export const MessageStatus = z.enum([
  'received', 'draft', 'held', 'scheduled', 'queued', 'sending', 'sent', 'delivered', 'failed', 'suppressed', 'discarded', 'shadow',
])
export type MessageStatus = z.infer<typeof MessageStatus>

export const AuthorKind = z.enum(['customer', 'ai', 'staff', 'system', 'agent'])
export type AuthorKind = z.infer<typeof AuthorKind>

/** Autonomy levels (design 13.1). */
export const AutonomyLevel = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)])
export type AutonomyLevel = z.infer<typeof AutonomyLevel>
export const AUTONOMY_NAMES: Record<AutonomyLevel, string> = {
  0: 'Shadow',
  1: 'Approve',
  2: 'Veto window',
  3: 'Autonomous',
}

export const ExceptionReason = z.enum([
  'asked_for_person', 'over_limit', 'risk_flag', 'low_confidence', 'needs_consultant',
  'clarification_stalled', 'check_failed', 'autonomy_approval', 'shadow_review', 'knowledge_gap', 'identity',
])
export type ExceptionReason = z.infer<typeof ExceptionReason>

export const TaskStage = z.enum([
  'clarifying', 'assessing', 'quoted', 'queued', 'running', 'verifying', 'awaiting_customer',
  'with_consultant', 'approved', 'delivering', 'done', 'cancelled', 'failed',
])
export type TaskStage = z.infer<typeof TaskStage>

export const TaskRoute = z.enum(['ai', 'ai_signoff', 'consultant'])
export type TaskRoute = z.infer<typeof TaskRoute>

export const TaskKind = z.enum(['coding', 'business', 'consultant'])
export type TaskKind = z.infer<typeof TaskKind>

export const QuestionType = z.enum(['noul', 'choice', 'score'])
export type QuestionType = z.infer<typeof QuestionType>

export const DecidedBy = z.enum(['rule', 'jev', 'llm', 'none'])
export type DecidedBy = z.infer<typeof DecidedBy>

export const ActorKind = z.enum(['staff', 'operator', 'ai', 'agent', 'system', 'customer', 'import'])
export type ActorKind = z.infer<typeof ActorKind>

/** The eight console spaces plus Settings (design 15.4). */
export const Space = z.enum(['today', 'exceptions', 'live', 'crm', 'tasks', 'studio', 'improve', 'analytics', 'settings'])
export type Space = z.infer<typeof Space>

export const SPACES_BY_ROLE: Record<StaffRole, Space[]> = {
  admin: ['today', 'exceptions', 'live', 'crm', 'tasks', 'studio', 'improve', 'analytics', 'settings'],
  supervisor: ['today', 'exceptions', 'live', 'crm', 'tasks', 'studio', 'improve', 'analytics'],
  specialist: ['today', 'exceptions', 'live', 'crm', 'tasks'],
  consultant: ['today', 'crm', 'tasks'],
}
